
import { List, Map } from 'immutable';
import pluralize from 'pluralize';

import { getVariantNameError } from './validations/variations';
import { getProfileSections } from './profiles/sections';
import { sortCaseIgnore } from './array';
import { capitalize } from './string';
import { isTruthy } from './bool';
import { shapeId } from './listings/listings';
import { getSize } from './iterable/getSize';
import { reduce } from './iterable/reduce';

import { AMOUNT, COLLECTION } from '../constants/product';
import { CHANNEL_DATA_KEYS } from '../constants/data';
import { EMPTY, SEPARATOR } from '../constants';
import { PROFILE_TYPE } from '../constants/profiles';
import { ATTRIBUTE } from '../constants/attributes';
import { EBAY } from '../constants/channels';
import { DATA } from '../data';

const EMPTY_DATA = Map({ byId: EMPTY.MAP, options: EMPTY.LIST });
const EMPTY_PROFILES = Map({ byId: EMPTY.MAP, ids: EMPTY.LIST });

export function isChannelDataLoaded({ channel, marketplaceId }) {
  function reduceKeysForLoaded(data) {
    return function reduceKeys(loaded, key) {
      return loaded && data.hasOwnProperty(key);
    };
  }

  switch (channel) {
    case EBAY: {
      return (
        DATA.hasOwnProperty(channel) &&
        DATA[channel].hasOwnProperty(marketplaceId) &&
        CHANNEL_DATA_KEYS[channel].reduce(reduceKeysForLoaded(DATA[channel][marketplaceId]), true)
      );
    }

    default: {
      return (
        !CHANNEL_DATA_KEYS.hasOwnProperty(channel) || (
          DATA.hasOwnProperty(channel) &&
          CHANNEL_DATA_KEYS[channel].reduce(reduceKeysForLoaded(DATA[channel]), true)
        )
      );
    }
  }
}

export function addChannelData({ channel, data, key, marketplaceId }) {
  switch (channel) {
    case EBAY: {
      if (!DATA.hasOwnProperty(channel)) {
        DATA[channel] = {};
      }
      if (!DATA[channel].hasOwnProperty(marketplaceId)) {
        DATA[channel][marketplaceId] = {};
      }

      DATA[channel][marketplaceId][key] = data;
      break;
    }

    default: {
      if (!DATA.hasOwnProperty(channel)) {
        DATA[channel] = {};
      }

      DATA[channel][key] = data;
      break;
    }
  }
}

export function addTaxonomyData({ category, channel, marketplaceId, data }) {
  if (!DATA.hasOwnProperty(channel)) {
    DATA[channel] = {};
  }

  if (!DATA[channel].hasOwnProperty(marketplaceId)) {
    DATA[channel][marketplaceId] = {};
  }

  if (!DATA[channel][marketplaceId].hasOwnProperty('CATEGORIES')) {
    DATA[channel][marketplaceId].CATEGORIES = {};
  }

  DATA[channel][marketplaceId].CATEGORIES[category] = data;
}

export const shapeDataForApp = {
  collections(shopCollections = []) {
    function reduceCollections(result, { label, type, value }) {
      function updateOptions(options) {
        return type === COLLECTION.SMART ? options : options.push(value);
      }

      function updateById(byId) {
        return byId.set(value, Map({ label, type }));
      }

      return result
        .update('options', updateOptions)
        .update('byId', updateById);
    }

    function sortCollections(collectionsById) {
      return function sort(x, y) {
        return sortCaseIgnore(
          collectionsById.getIn([x, 'label']),
          collectionsById.getIn([y, 'label'])
        );
      };
    }

    let collections = shopCollections.reduce(reduceCollections, EMPTY_DATA);

    collections = collections.set('options',
      collections.get('options').sort(sortCollections(collections.get('byId')))
    );

    return collections;
  },
  countryData(data) {
    function addCountry(result, { code, name }) {
      result.COUNTRIES_MAP[code] = name;
      result.COUNTRIES_LIST.push({ code, name });
      return result;
    }

    return data.reduce(addCountry, { COUNTRIES_LIST: [], COUNTRIES_MAP: {}});
  },
  ebayPolicies(shopPolicies = []) {
    function reducePolicies(result, policy) {
      const { channel_policy_id: channelPolicyId, name } = policy;

      function updateOptions(options) {
        return options.push(channelPolicyId);
      }

      function updateById(byId) {
        return byId.set(channelPolicyId, name);
      }

      return result
        .update('byId', updateById)
        .update('options', updateOptions);
    }

    function sortPolicies(policiesById = {}) {
      return function sort(x, y) {
        return sortCaseIgnore(policiesById.get(x), policiesById.get(y));
      };
    }

    const policies = shopPolicies.reduce(reducePolicies, EMPTY_DATA);

    return policies.set('options', policies.get('options').sort(sortPolicies(policies.get('byId'))));
  },
  ebayShippingPolicies(shopPolicies = []) {
    function reducePolicies(result, policy) {
      const { channel_policy_id: channelPolicyId, name, required_attributes: requiredAttributes } = policy;

      function updateOptions(options) {
        return options.push(channelPolicyId);
      }

      function updateById(byId) {
        return byId.set(channelPolicyId, name);
      }

      function reduceRequiredAttributes(validations, value, attribute) {
        if (!value) return validations;

        switch (attribute) {
          case 'location': {
            return validations.add(ATTRIBUTE.LOCATION);
          }

          case 'postal_code': {
            return validations.add(ATTRIBUTE.POSTAL_CODE);
          }

          case 'weight': {
            return validations.add(ATTRIBUTE.WEIGHT);
          }

          default: {
            return validations;
          }
        }
      }

      result.policies = result.policies
        .update('byId', updateById)
        .update('options', updateOptions);

      if (getSize(requiredAttributes) > 0) {
        result.validations = result.validations.set(
          channelPolicyId,
          reduce(requiredAttributes, EMPTY.SET, reduceRequiredAttributes)
        );
      }

      return result;
    }

    function sortPolicies(policiesById = {}) {
      return function sort(x, y) {
        return sortCaseIgnore(policiesById.get(x), policiesById.get(y));
      };
    }

    const { policies, validations } = shopPolicies.reduce(
      reducePolicies,
      {
        policies: EMPTY_DATA,
        validations: EMPTY.MAP,
      },
    );

    return {
      policies: policies.set('options', policies.get('options').sort(sortPolicies(policies.get('byId')))),
      validations,
    };
  },
  processingProfiles(processingProfiles = []) {
    function reduceProcessingProfiles(result, profile) {
      const {
        processing_days_display_label: label,
        readiness_state: state,
        readiness_state_id: stateId,
      } = profile;

      const id = shapeId(stateId);
      const name = `${capitalize(state.replace(/_/g, SEPARATOR.SPACE))}: ${label}`;

      function updateById(byId) {
        return byId.set(id, Map({ name, state }));
      }

      function updateOptions(options) {
        return options.push(id);
      }

      return result
        .update('byId', updateById)
        .update('options', updateOptions);
    }

    const profiles = processingProfiles.reduce(reduceProcessingProfiles, EMPTY_DATA);

    function sortProfiles(profilesById) {
      return function sort(x, y) {
        return sortCaseIgnore(profilesById.getIn([x, 'name']), profilesById.getIn([y, 'name']));
      };
    }

    return profiles.set('options', profiles.get('options').sort(sortProfiles(profiles.get('byId'))));

  },
  productionPartners(productionPartners = []) {
    function reduceProductionPartners(result, productionPartner) {
      const { partner_name: name, production_partner_id: productionPartnerId } = productionPartner;
      const id = parseInt(productionPartnerId);

      function updateOptions(options) {
        return options.push(id);
      }

      function updateById(byId) {
        return byId.set(id, name);
      }

      return result
        .update('byId', updateById)
        .update('options', updateOptions);
    }

    const partners = productionPartners.reduce(reduceProductionPartners, EMPTY_DATA);

    function sortProductionPartners(partnersById = {}) {
      return function sort(x, y) {
        return sortCaseIgnore(partnersById.get(x), partnersById.get(y));
      };
    }

    return partners.set('options', partners.get('options').sort(sortProductionPartners(partners.get('byId'))));
  },
  productTypes(productTypes = []) {
    return List(productTypes).filter(isTruthy).sort(sortCaseIgnore);
  },
  profiles({ channel, data, type }) {
    function reduceProfiles(result, item) {
      const { id } = item;

      function updateById(byId) {
        const { connected_listings: connectedListings, name, sections } = item;

        let profile = Map({
          id,
          connectedListings: connectedListings || 0,
          name,
          sections: getProfileSections({ channel, type, value: sections }),
          type,
        });

        if (type === PROFILE_TYPE.STANDARD) {
          const { product_template_id: templateId, template_mode: templateMode } = item;

          profile = profile
            .set('templateId', templateId)
            .set('templateMode', templateMode);
        }

        return byId.set(id, profile);
      }

      function updateIds(ids) {
        return ids.push(id);
      }

      return result
        .update('byId', updateById)
        .update('ids', updateIds);
    }

    const profiles = data.reduce(reduceProfiles, EMPTY_PROFILES);

    function sortProfiles(profilesById) {
      return function sort(x, y) {
        return sortCaseIgnore(profilesById.getIn([x, 'name']), profilesById.getIn([y, 'name']));
      };
    }

    return profiles.set('ids', profiles.get('ids').sort(sortProfiles(profiles.get('byId'))));
  },
  returnPolicies(returnPolicies = []) {
    function getReturnPolicyDescription(policy = {}) {
      const {
        accepts_returns: acceptsReturns,
        accepts_exchanges: acceptsExchanges,
        return_deadline: returnDeadline,
      } = policy;

      if (acceptsReturns && acceptsExchanges) {
        return `${pluralize('day', returnDeadline, true)} to return or exchange`;
      }

      if (acceptsExchanges) {
        return `${pluralize('day', returnDeadline, true)} to exchange`;
      }

      if (acceptsReturns) {
        return `${pluralize('day', returnDeadline, true)} to return`;
      }

      return 'No returns or exchanges';
    }

    function reduceReturnPolicies(result, policy) {
      const { return_policy_id: returnPolicyId, return_deadline: returnDeadline } = policy;

      function updateOptions(options) {
        return options.push(returnPolicyId);
      }

      function updateById(byId) {
        return byId.set(returnPolicyId, getReturnPolicyDescription(policy));
      }

      result.deadlines = result.deadlines.set(returnPolicyId, returnDeadline);
      result.policies = result.policies
        .update('byId', updateById)
        .update('options', updateOptions);

      return result;
    }

    const { deadlines, policies } = returnPolicies.reduce(
      reduceReturnPolicies,
      {
        deadlines: EMPTY.MAP,
        policies: EMPTY_DATA.setIn(['byId', AMOUNT.ONE], 'No policy'),
      },
    );

    function sortReturnPolicies(x, y) {
      if (!deadlines.get(x)) return -1;

      if (!deadlines.get(y)) return 1;

      return deadlines.get(x) - deadlines.get(y);
    }

    return policies.set('options', policies.get('options').sort(sortReturnPolicies).unshift(AMOUNT.ONE));
  },
  sections(shopSections = {}) {
    function getByIdAndOptions(source) {
      return function reduceSections(result, id) {
        function updateOptions(options) {
          return options.push(id);
        }

        function updateById(byId) {
          return byId.set(id, source[id]);
        }

        return result
          .update('options', updateOptions)
          .update('byId', updateById);
      };
    }

    function sortSections(sectionsById = {}) {
      return function sort(x, y) {
        return sortCaseIgnore(sectionsById.get(x), sectionsById.get(y));
      };
    }

    const { shopId, ids: sectionIds = [], ...rest } = shopSections;
    const sections = sectionIds.reduce(getByIdAndOptions(rest), EMPTY_DATA);

    return sections.set('options', sections.get('options').sort(sortSections(sections.get('byId'))));
  },
  shippingProfiles(shippingTemplates = []) {
    function reduceShippingProfiles(result, profile) {
      const { profile_type: profileType, shipping_template_id: profileId, title: name } = profile;
      const id = shapeId(profileId);

      function updateById(byId) {
        return byId.set(id, Map({ profileType: shapeId(profileType), name }));
      }

      function updateOptions(options) {
        return options.push(id);
      }

      return result
        .update('byId', updateById)
        .update('options', updateOptions);
    }

    const profiles = shippingTemplates.reduce(reduceShippingProfiles, EMPTY_DATA);

    function sortProfiles(profilesById) {
      return function sort(x, y) {
        return sortCaseIgnore(profilesById.getIn([x, 'name']), profilesById.getIn([y, 'name']));
      };
    }

    return profiles.set('options', profiles.get('options').sort(sortProfiles(profiles.get('byId'))));
  },
  storeCategories(storeCategories = []) {
    function reduceCategories(result, category) {
      const { channel_custom_category_id: categoryId, name } = category;

      function updateOptions(options) {
        return options.push(categoryId);
      }

      function updateById(byId) {
        return byId.set(categoryId, name);
      }

      return result
        .update('byId', updateById)
        .update('options', updateOptions);
    }

    function sortCategories(categoriesById = {}) {
      return function sort(x, y) {
        if (x === AMOUNT.ONE) return -1;

        if (y === AMOUNT.ONE) return 1;

        return sortCaseIgnore(categoriesById.get(x), categoriesById.get(y));
      };
    }

    const categories = storeCategories.reduce(reduceCategories, EMPTY_DATA);

    return categories.set('options', categories.get('options').sort(sortCategories(categories.get('byId'))));
  },
  validations({ channel, data }) {
    switch (channel) {
      case EBAY: {
        const { DetailVersion, UpdateTime, ...rest } = data;

        return Map(rest);
      }

      default: {
        return EMPTY.MAP;
      }
    }
  },
  variations({ channel, data }) {
    const { options, variants } = data;

    function reduceOptions(result, items, variant) {
      return result.set(variant, List(items.map(Map)));
    }

    function reduceVariants(result, variant) {
      if (
        !result.values.has(variant.value) &&
        !getVariantNameError({ channel, name: variant.value })
      ) {
        result.values = result.values.add(variant.value);
        result.variants = result.variants.push(Map(variant));
      }

      return result;
    }

    return Map({
      variants: variants.reduce(reduceVariants, { values: EMPTY.SET, variants: EMPTY.LIST }).variants,
      options: reduce(options, EMPTY.MAP, reduceOptions),
    });
  },
  vendors(vendors = []) {
    return List(vendors).filter(isTruthy).sort(sortCaseIgnore);
  },
};
